/**
 * Theme Manager
 *
 * Handles light/dark theme switching with localStorage persistence.
 * Module-scoped `currentTheme` singleton synced to localStorage, restored by `initTheme()`.
 */

import { storageKey } from '@/lib/infra/storage';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = storageKey('app_theme');
const LIGHT_CLASS = 'light';

let currentTheme: Theme = 'dark';

/**
 * Apply theme class to document
 */
function applyTheme(theme: Theme): void {
  if (theme === 'light') {
    document.documentElement.classList.add(LIGHT_CLASS);
  } else {
    document.documentElement.classList.remove(LIGHT_CLASS);
  }
}

/**
 * Initialize theme from localStorage or system preference
 */
export function initTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY);

  if (saved === 'light' || saved === 'dark') {
    currentTheme = saved;
  } else {
    // Check system preference if no saved preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    currentTheme = prefersDark ? 'dark' : 'light';
  }

  applyTheme(currentTheme);
  return currentTheme;
}

/**
 * Get current theme
 */
export function getTheme(): Theme {
  return currentTheme;
}

/**
 * Whether a theme has been CHOSEN, as opposed to inferred from the OS.
 *
 * Following the system is the absence of a stored choice, so this is the
 * predicate for "is the OS still in charge" — `initTheme` treats an
 * unparseable value the same as an absent one, and so does this, keeping one
 * meaning for the stored value across every reader.
 */
export function hasStoredTheme(): boolean {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'light' || saved === 'dark';
}

/**
 * Track an OS preference change WITHOUT recording it as a choice.
 *
 * Persisting here would end the very system-following this exists to track:
 * one flip at sunset and the app is pinned forever, with no control anywhere
 * to undo it. The look changes; the absence of a stored choice does not.
 */
export function followSystemTheme(theme: Theme): void {
  currentTheme = theme;
  applyTheme(theme);
}

/**
 * Set theme and save to localStorage
 */
export function setTheme(theme: Theme): void {
  currentTheme = theme;
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

/**
 * Toggle between light and dark
 */
export function toggleTheme(): Theme {
  const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
  setTheme(newTheme);
  return newTheme;
}
